import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { RichTextEditor, RichTextDisplay } from "@/components/RichTextEditor";
import { ImageDropZone } from "@/components/ImageDropZone";
import { SlidePanel } from "@/components/SlidePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Plus, Edit, Trash2, Search, Users, FileUp, Shield, Heart, Footprints,
  Eye, Upload, X, FileText, ChevronDown, ChevronUp, Wand2, Lock, Coins, Save, BookOpen,
  Link2, Loader2
} from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import AICreateMode from "@/components/AICreateMode";
import EntityContextMenu from "@/components/EntityContextMenu";
import CharacterSessionHistory from "@/components/CharacterSessionHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RACES = ["Human", "Elf", "Dwarf", "Halfling", "Dragonborn", "Gnome", "Half-Elf", "Half-Orc", "Tiefling", "Aasimar", "Goliath", "Tabaxi", "Firbolg", "Kenku", "Half-Goblin", "Genasi", "Tortle", "Other"];
const CLASSES = ["Barbarian", "Bard", "Cleric", "Druid", "Fighter", "Monk", "Paladin", "Ranger", "Rogue", "Sorcerer", "Warlock", "Wizard", "Artificer", "Blood Hunter"];
const ABILITIES = ["STR", "DEX", "CON", "INT", "WIS", "CHA"];
const SKILLS_BY_ABILITY = {
  STR: ["Athletics"],
  DEX: ["Acrobatics", "Sleight of Hand", "Stealth"],
  CON: [],
  INT: ["Arcana", "History", "Investigation", "Nature", "Religion"],
  WIS: ["Animal Handling", "Insight", "Medicine", "Perception", "Survival"],
  CHA: ["Deception", "Intimidation", "Performance", "Persuasion"],
};

const EMPTY_SHEET = {
  edition: "5e_srd",
  abilities: { STR: 10, DEX: 10, CON: 10, INT: 10, WIS: 10, CHA: 10 },
  proficiency_bonus: 2,
  saving_throws: [],
  skill_proficiencies: [],
  spell_slots: "",
  features: "",
  equipment: "",
  notes: "",
};

const CharactersPage = () => {
  const { campaignId } = useParams();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "all";
  const isCommoner = (user?.tier || "commoner") === "commoner";
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [createMode, setCreateMode] = useState("manual");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState(initialType);
  const [uploadingImageFor, setUploadingImageFor] = useState(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [showSheet, setShowSheet] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [sheetUrl, setSheetUrl] = useState("");
  const [importingUrl, setImportingUrl] = useState(false);

  const pdfInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "", description: "", character_type: "pc",
    race: "", character_class: "", level: 1, location_id: "",
    armor_class: "", hit_points: "", speed: "30", initiative: "",
    character_sheet: null, player_name: ""
  });

  useEffect(() => { fetchData(); }, [campaignId]);

  // Handle creation-mode chooser pick → open the main dialog primed for the picked flow.
  const handlePickCreateMode = (mode) => {
    resetForm();
    setChooseModeOpen(false);
    if (mode === "creator") {
      setCreateMode("ai");
      setShowSheet(true);
      setFormData(p => ({ ...p, character_type: "pc" }));
    } else if (mode === "quick-npc") {
      setCreateMode("manual");
      setShowSheet(false);
      setFormData(p => ({ ...p, character_type: "npc" }));
    } else if (mode === "manual") {
      setCreateMode("manual");
      setShowSheet(true);
      setFormData(p => ({ ...p, character_type: "pc" }));
    } else if (mode === "import-pdf") {
      setCreateMode("manual");
      setShowSheet(false);
      setFormData(p => ({ ...p, character_type: "pc" }));
      // Open native PDF picker on next tick
      setTimeout(() => pdfInputRef.current?.click(), 100);
    }
    setDialogOpen(true);
  };

  const fetchData = async () => {
    try {
      const [campRes, charsRes, locsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/characters`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ]);
      setCampaign(campRes.data);
      setCharacters(charsRes.data);
      setLocations(locsRes.data);
      if (selectedCharacter) {
        const updated = charsRes.data.find(c => c.id === selectedCharacter.id);
        if (updated) setSelectedCharacter(updated);
      }
    } catch { toast.error("Failed to load characters"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData, campaign_id: campaignId,
        character_sheet: showSheet ? formData.character_sheet : null,
      };
      let charId;
      if (editing) {
        await axios.put(`${API}/characters/${editing.id}`, payload, { withCredentials: true });
        charId = editing.id;
        toast.success("Character updated");
      } else {
        const res = await axios.post(`${API}/characters`, payload, { withCredentials: true });
        charId = res.data.id;
        toast.success("Character created");
      }
      // Upload image if selected
      if (imageFile && charId) {
        const fd = new FormData();
        fd.append("file", imageFile);
        await axios.post(`${API}/characters/${charId}/upload`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      }
      // Upload PDF if selected
      if (pdfFile && charId) {
        const fd = new FormData();
        fd.append("file", pdfFile);
        await axios.post(`${API}/characters/${charId}/upload-pdf`, fd, {
          withCredentials: true, headers: { "Content-Type": "multipart/form-data" }
        });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save character"); }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Character", "Are you sure? This cannot be undone.");
    if (!ok) return;
    try {
      await axios.delete(`${API}/characters/${id}`, { withCredentials: true });
      toast.success("Character deleted");
      if (selectedCharacter?.id === id) setSelectedCharacter(null);
      fetchData();
    } catch { toast.error("Failed to delete character"); }
  };

  const handleImageSelect = (file) => {
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handlePdfSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === "application/pdf") {
      setPdfFile(file);
    } else {
      toast.error("Please select a PDF file");
    }
  };

  const handleImportSheetUrl = async () => {
    const url = sheetUrl.trim();
    if (!url) return;
    setImportingUrl(true);
    try {
      const res = await axios.post(
        `${API}/campaigns/${campaignId}/import-dndb`,
        { url },
        { withCredentials: true }
      );
      const c = res.data || {};
      // Pre-fill the create form with whatever the import returned.
      setFormData(prev => ({
        ...prev,
        name: c.name || prev.name,
        race: c.race || prev.race,
        character_class: c.character_class || prev.character_class,
        level: c.level || prev.level,
        armor_class: c.armor_class || prev.armor_class,
        hit_points: c.hit_points || prev.hit_points,
        speed: c.speed || prev.speed,
        initiative: c.initiative || prev.initiative,
        description: c.description || prev.description,
        player_name: c.player_name || prev.player_name,
      }));
      setShowSheet(true);
      setCreateMode("manual");
      setSheetUrl("");
      toast.success(`Imported ${c.name || "character"} — review and Save to finish.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't import that URL. Double-check it and try again.");
    } finally {
      setImportingUrl(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setImageFile(null);
    setImagePreview(null);
    setPdfFile(null);
    setSheetUrl("");
    setShowSheet(false);
    setSheetExpanded(false);
    setFormData({
      name: "", description: "", character_type: "pc",
      race: "", character_class: "", level: 1, location_id: "",
      armor_class: "", hit_points: "", speed: "30", initiative: "",
      character_sheet: null, player_name: ""
    });
  };

  const openEdit = (character) => {
    setEditing(character);
    setImageFile(null);
    setImagePreview(character.image_path ? `${API}/files/${character.image_path}` : null);
    setPdfFile(null);
    const hasSheet = character.character_sheet && Object.keys(character.character_sheet).length > 0;
    setShowSheet(hasSheet);
    setFormData({
      name: character.name, description: character.description || "",
      character_type: character.character_type || "pc",
      race: character.race || "", character_class: character.character_class || "",
      level: character.level || 1, location_id: character.location_id || "",
      armor_class: character.armor_class || "", hit_points: character.hit_points || "",
      speed: character.speed || "30", initiative: character.initiative || "",
      character_sheet: character.character_sheet || { ...EMPTY_SHEET },
      player_name: character.player_name || ""
    });
    setSelectedCharacter(null);
    setDialogOpen(true);
  };

  const updateSheet = (key, value) => {
    setFormData(prev => ({
      ...prev,
      character_sheet: { ...(prev.character_sheet || EMPTY_SHEET), [key]: value }
    }));
  };

  const updateAbility = (ability, value) => {
    const abilities = { ...(formData.character_sheet?.abilities || EMPTY_SHEET.abilities) };
    abilities[ability] = parseInt(value) || 10;
    updateSheet("abilities", abilities);
  };

  const toggleProficiency = (list, item) => {
    const current = formData.character_sheet?.[list] || [];
    const updated = current.includes(item) ? current.filter(i => i !== item) : [...current, item];
    updateSheet(list, updated);
  };

  const getModifier = (score) => {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  const getImageUrl = (character) => character.image_path ? `${API}/files/${character.image_path}` : null;

  const filtered = characters.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = filterType === "all" || c.character_type === filterType;
    return matchSearch && matchType;
  });

  if (loading) return <Layout campaignName="Loading..."><div className="flex justify-center py-20"><div className="spinner" /></div></Layout>;

  return (
    <Layout campaignName={campaign?.name}>
      <div data-testid="characters-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">Characters</h1>
            <p className="text-muted-foreground text-sm">Track heroes, villains, and everyone between</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) resetForm(); setDialogOpen(v); }}>
            <Button onClick={() => { resetForm(); setCreateMode("manual"); setShowSheet(true); setFormData(p => ({ ...p, character_type: "pc" })); setDialogOpen(true); }} data-testid="create-character-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> New Character
            </Button>
            <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{editing ? "Edit Character" : "New Character"}</DialogTitle>
              </DialogHeader>
              {!editing && (
                <div className="flex gap-2 border-b border-border pb-3 mb-1">
                  <Button variant={createMode === "manual" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("manual")} data-testid="mode-manual">
                    <Plus className="w-3 h-3 mr-1" /> Create Manually
                  </Button>
                  {!isCommoner && (
                    <Button variant={createMode === "ai" ? "default" : "outline"} size="sm" className="rounded-sm text-xs" onClick={() => setCreateMode("ai")} data-testid="mode-ai">
                      <Wand2 className="w-3 h-3 mr-1" /> Use AI
                    </Button>
                  )}
                  {isCommoner && (
                    <Button variant="outline" size="sm" className="rounded-sm text-xs opacity-60 cursor-not-allowed" disabled data-testid="mode-ai-locked">
                      <Lock className="w-3 h-3 mr-1" /> AI (Upgrade)
                    </Button>
                  )}
                </div>
              )}
              {!editing && createMode === "ai" && (
                <AICreateMode entityType="character" campaignId={campaignId} onGenerated={(data) => {
                  setFormData(prev => ({ ...prev, ...data }));
                  setCreateMode("manual");
                }} />
              )}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" style={{ display: (!editing && createMode === "ai") ? "none" : undefined }}>
                {/* Image Upload */}
                <div>
                  <label className="text-sm font-medium block mb-2">Character Image</label>
                  <ImageDropZone
                    compact
                    onFileSelect={handleImageSelect}
                    preview={imagePreview}
                    fileName={imageFile?.name}
                    testId="character-image-picker"
                    onClear={() => { setImageFile(null); setImagePreview(null); }}
                  />
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-sm font-medium block mb-1">Name *</label>
                    <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Gandalf the Grey" required data-testid="character-name-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Type</label>
                    <Select value={formData.character_type} onValueChange={v => setFormData({ ...formData, character_type: v })}>
                      <SelectTrigger data-testid="character-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pc">Player Character</SelectItem>
                        <SelectItem value="npc">NPC</SelectItem>
                        <SelectItem value="companion">Companion</SelectItem>
                        <SelectItem value="summon">Summon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Player Name</label>
                    <Input value={formData.player_name} onChange={e => setFormData({ ...formData, player_name: e.target.value })} placeholder="John" data-testid="player-name-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Race</label>
                    <Select value={formData.race || "__none__"} onValueChange={v => setFormData({ ...formData, race: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="character-race-select"><SelectValue placeholder="Select race" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select race</SelectItem>
                        {RACES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Class</label>
                    <Select value={formData.character_class || "__none__"} onValueChange={v => setFormData({ ...formData, character_class: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="character-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select class</SelectItem>
                        {CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Level</label>
                    <Input type="number" min="1" max="20" value={formData.level} onChange={e => setFormData({ ...formData, level: parseInt(e.target.value) || 1 })} data-testid="character-level-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Location</label>
                    <Select value={formData.location_id || "__none__"} onValueChange={v => setFormData({ ...formData, location_id: v === "__none__" ? "" : v })}>
                      <SelectTrigger data-testid="character-location-select"><SelectValue placeholder="Location" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Stats Row */}
                <div>
                  <label className="text-sm font-medium block mb-2">Combat Stats</label>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Shield className="w-3 h-3" /> AC</label>
                      <Input className="text-center text-sm h-9" value={formData.armor_class} onChange={e => setFormData({ ...formData, armor_class: e.target.value })} placeholder="15" data-testid="stat-ac" />
                    </div>
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Heart className="w-3 h-3" /> HP</label>
                      <Input className="text-center text-sm h-9" value={formData.hit_points} onChange={e => setFormData({ ...formData, hit_points: e.target.value })} placeholder="45" data-testid="stat-hp" />
                    </div>
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Footprints className="w-3 h-3" /> Speed</label>
                      <Input className="text-center text-sm h-9" value={formData.speed} onChange={e => setFormData({ ...formData, speed: e.target.value })} placeholder="30ft" data-testid="stat-speed" />
                    </div>
                    <div className="text-center">
                      <label className="text-xs text-muted-foreground flex items-center justify-center gap-1 mb-1"><Eye className="w-3 h-3" /> Init</label>
                      <Input className="text-center text-sm h-9" value={formData.initiative} onChange={e => setFormData({ ...formData, initiative: e.target.value })} placeholder="+2" data-testid="stat-init" />
                    </div>
                  </div>
                </div>

                {/* Quick Import — paste a character sheet URL */}
                {!editing && formData.character_type === "pc" && (
                  <div className="rounded-sm border border-primary/20 bg-primary/5 p-3" data-testid="char-url-import-chip">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-4 h-4 text-primary" />
                      <span className="text-xs font-semibold text-primary">Got a sheet URL? Paste it.</span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        value={sheetUrl}
                        onChange={(e) => setSheetUrl(e.target.value)}
                        placeholder="https://...character-sheet-url"
                        className="flex-1"
                        data-testid="char-url-import-input"
                        disabled={importingUrl}
                      />
                      <Button
                        type="button"
                        onClick={handleImportSheetUrl}
                        disabled={!sheetUrl.trim() || importingUrl}
                        size="sm"
                        className="rounded-sm"
                        data-testid="char-url-import-button"
                      >
                        {importingUrl
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Importing</>
                          : <><Upload className="w-3 h-3 mr-1" /> Import</>
                        }
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      We'll pull name, race, class, level, ability scores, and backstory automatically — review before saving.
                    </p>
                  </div>
                )}

                {/* PDF Upload */}
                <div>
                  <label className="text-sm font-medium block mb-2">Character Sheet PDF</label>
                  <div
                    className="border border-dashed border-border rounded-sm p-3 text-center cursor-pointer hover:border-primary/40 transition-colors"
                    onClick={() => pdfInputRef.current?.click()}
                    data-testid="pdf-upload-area"
                  >
                    {pdfFile ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-primary">
                        <FileText className="w-4 h-4" /> {pdfFile.name}
                        <button type="button" onClick={e => { e.stopPropagation(); setPdfFile(null); }} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                      </div>
                    ) : editing?.pdf_path ? (
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <FileText className="w-4 h-4" /> PDF attached — click to replace
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                        <FileUp className="w-4 h-4" /> Upload PDF character sheet (optional)
                      </div>
                    )}
                  </div>
                  <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden" onChange={handlePdfSelect} />
                </div>

                {/* Optional Character Sheet Toggle */}
                {formData.character_type === "pc" && (
                  <div className="border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">Full Character Sheet</span>
                        <span className="text-xs text-muted-foreground block">Ability scores, skills, proficiencies</span>
                      </div>
                      <Switch checked={showSheet} onCheckedChange={(v) => {
                        setShowSheet(v);
                        if (v && !formData.character_sheet) {
                          setFormData(prev => ({ ...prev, character_sheet: { ...EMPTY_SHEET } }));
                        }
                      }} data-testid="toggle-character-sheet" />
                    </div>

                    {showSheet && (
                      <div className="mt-4 space-y-4">
                        {/* Edition switch (5e SRD / 2024 One D&D) */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ruleset</label>
                          <Select
                            value={formData.character_sheet?.edition || "5e_srd"}
                            onValueChange={(v) => updateSheet("edition", v)}
                          >
                            <SelectTrigger className="w-44 h-8 text-xs" data-testid="character-edition-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover border-border">
                              <SelectItem value="5e_srd">D&amp;D 5e (SRD)</SelectItem>
                              <SelectItem value="5e_2024">D&amp;D 5e 2024 (One D&amp;D)</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.character_sheet?.edition === "5e_2024" && (
                            <span className="text-[10px] uppercase tracking-wider text-primary/80 bg-primary/10 px-2 py-0.5 rounded-sm border border-primary/20">2024</span>
                          )}
                        </div>

                        {/* Ability Scores */}
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-2">Ability Scores</label>
                          <div className="grid grid-cols-6 gap-2">
                            {ABILITIES.map(ab => (
                              <div key={ab} className="text-center">
                                <label className="text-xs font-bold text-foreground">{ab}</label>
                                <Input
                                  className="text-center text-sm h-9 mt-1"
                                  type="number" min="1" max="30"
                                  value={formData.character_sheet?.abilities?.[ab] || 10}
                                  onChange={e => updateAbility(ab, e.target.value)}
                                  data-testid={`ability-${ab.toLowerCase()}`}
                                />
                                <span className="text-xs text-primary">{getModifier(formData.character_sheet?.abilities?.[ab] || 10)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Proficiency Bonus */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs font-medium text-muted-foreground">Proficiency Bonus</label>
                          <Input
                            className="w-16 text-center text-sm h-8"
                            value={formData.character_sheet?.proficiency_bonus || 2}
                            onChange={e => updateSheet("proficiency_bonus", parseInt(e.target.value) || 2)}
                            data-testid="proficiency-bonus"
                          />
                        </div>

                        {/* Saving Throws */}
                        <div>
                          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1">Saving Throws</label>
                          <div className="flex flex-wrap gap-1.5">
                            {ABILITIES.map(ab => (
                              <button type="button" key={ab}
                                className={`px-2 py-1 rounded-sm text-xs border transition-colors ${
                                  (formData.character_sheet?.saving_throws || []).includes(ab)
                                    ? "bg-primary/20 border-primary/40 text-primary" : "bg-muted/10 border-border text-muted-foreground"
                                }`}
                                onClick={() => toggleProficiency("saving_throws", ab)}
                                data-testid={`save-${ab.toLowerCase()}`}
                              >{ab}</button>
                            ))}
                          </div>
                        </div>

                        {/* Skills */}
                        <div>
                          <button type="button" className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1" onClick={() => setSheetExpanded(!sheetExpanded)}>
                            Skills & Proficiencies {sheetExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </button>
                          {sheetExpanded && (
                            <div className="space-y-2 mt-2">
                              {Object.entries(SKILLS_BY_ABILITY).map(([ab, skills]) => (
                                skills.length > 0 && (
                                  <div key={ab}>
                                    <span className="text-xs font-bold text-foreground/70">{ab}</span>
                                    <div className="flex flex-wrap gap-1 mt-0.5">
                                      {skills.map(skill => (
                                        <button type="button" key={skill}
                                          className={`px-2 py-0.5 rounded-sm text-xs border transition-colors ${
                                            (formData.character_sheet?.skill_proficiencies || []).includes(skill)
                                              ? "bg-primary/20 border-primary/40 text-primary" : "bg-muted/10 border-border text-muted-foreground"
                                          }`}
                                          onClick={() => toggleProficiency("skill_proficiencies", skill)}
                                        >{skill}</button>
                                      ))}
                                    </div>
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Extra fields */}
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Spell Slots / Features</label>
                          <Input value={formData.character_sheet?.spell_slots || ""} onChange={e => updateSheet("spell_slots", e.target.value)} placeholder="e.g. 4/3/3/1 or Rage 3/day" data-testid="spell-slots" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground block mb-1">Equipment</label>
                          <Input value={formData.character_sheet?.equipment || ""} onChange={e => updateSheet("equipment", e.target.value)} placeholder="Longsword, Shield, Chain Mail..." data-testid="equipment" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="text-sm font-medium block mb-2">Description / Backstory</label>
                  <RichTextEditor value={formData.description} onChange={v => setFormData({ ...formData, description: v })} placeholder="Describe this character..." />
                </div>

                <Button type="submit" className="w-full" data-testid="save-character-button">
                  {editing ? "Save Changes" : "Create Character"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search characters..." className="pl-10" data-testid="search-characters" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="pc">PCs</SelectItem>
              <SelectItem value="npc">NPCs</SelectItem>
              <SelectItem value="companion">Companions</SelectItem>
              <SelectItem value="summon">Summons</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {characters.length === 0 ? (
          <div className="empty-state">
            <Users className="empty-state-icon text-muted-foreground w-16 h-16 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-serif text-foreground mb-2">No characters yet</h3>
            <p className="text-muted-foreground mb-6">Add your first character</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-2" /> Create Character</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {filtered.map(char => (
              <EntityContextMenu
                key={char.id}
                onOpen={() => setSelectedCharacter(char)}
                onRename={() => openEdit(char)}
                onDelete={() => handleDelete(char.id)}
                pinPath={char.character_type === "companion" || char.character_type === "summon" ? "characters?type=companion" : "characters"}
                pinLabel={char.name}
                entityName={char.name}
              >
                <Card
                  className="bg-card border-border rounded-sm cursor-pointer entity-card overflow-hidden transition-colors hover:border-primary/40"
                  onClick={() => setSelectedCharacter(char)}
                  data-testid={`character-card-${char.id}`}
                >
                {getImageUrl(char) && (
                  <div className="h-36 w-full overflow-hidden">
                    <img src={getImageUrl(char)} alt={char.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      {!getImageUrl(char) && <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></div>}
                      <div>
                        <h3 className="font-serif text-lg text-foreground">{char.name}</h3>
                        <p className="text-xs text-muted-foreground capitalize">{({ pc: "Player Character", npc: "NPC", companion: "Companion", summon: "Summon" })[char.character_type] || "Character"}</p>
                      </div>
                    </div>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <VisibilityToggle entityType="characters" entityId={char.id} currentVisibility={char.visibility} onUpdate={fetchData} />
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    {char.race && <p>{char.race}{char.character_class ? ` · ${char.character_class} Lv.${char.level}` : ""}</p>}
                    {(char.armor_class || char.hit_points) && (
                      <div className="flex gap-3 text-xs">
                        {char.armor_class && <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> AC {char.armor_class}</span>}
                        {char.hit_points && <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> HP {char.hit_points}</span>}
                        {char.speed && <span className="flex items-center gap-1"><Footprints className="w-3 h-3" /> {char.speed}ft</span>}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              </EntityContextMenu>
            ))}
          </div>
        )}

        {/* Character Detail SlidePanel */}
        <SlidePanel open={!!selectedCharacter} onClose={() => setSelectedCharacter(null)} title={selectedCharacter?.name || "Character"}
          items={filtered} currentIndex={selectedCharacter ? filtered.findIndex(c => c.id === selectedCharacter.id) : -1}
          onNavigate={(i) => setSelectedCharacter(filtered[i])}
        >
          {selectedCharacter && (
            <div data-testid="character-detail-panel">
              {getImageUrl(selectedCharacter) && (
                <div className="h-48 w-full relative rounded-sm overflow-hidden mb-4">
                  <img src={getImageUrl(selectedCharacter)} alt={selectedCharacter.name} className="w-full h-full object-cover" crossOrigin="use-credentials" />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-4">
                {selectedCharacter.race && <span className="bg-muted/20 border border-border px-2 py-0.5 rounded-sm text-xs">{selectedCharacter.race}</span>}
                {selectedCharacter.character_class && <span className="bg-primary/10 border border-primary/20 text-primary px-2 py-0.5 rounded-sm text-xs">{selectedCharacter.character_class} Lv.{selectedCharacter.level}</span>}
                <span className="bg-muted/20 border border-border px-2 py-0.5 rounded-sm text-xs capitalize">{({ pc: "Player", npc: "NPC", companion: "Companion", summon: "Summon" })[selectedCharacter.character_type] || "Character"}</span>
              </div>

              {/* Stats */}
              {(selectedCharacter.armor_class || selectedCharacter.hit_points || selectedCharacter.speed || selectedCharacter.initiative) && (
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[
                    { label: "AC", value: selectedCharacter.armor_class, icon: Shield },
                    { label: "HP", value: selectedCharacter.hit_points, icon: Heart },
                    { label: "Speed", value: selectedCharacter.speed ? `${selectedCharacter.speed}ft` : "", icon: Footprints },
                    { label: "Init", value: selectedCharacter.initiative, icon: Eye },
                  ].filter(s => s.value).map(stat => (
                    <div key={stat.label} className="text-center p-2 rounded-sm border border-border bg-muted/10">
                      <stat.icon className="w-3.5 h-3.5 text-primary mx-auto mb-0.5" />
                      <div className="text-lg font-bold text-foreground">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Character Sheet */}
              {selectedCharacter.character_sheet && (
                <div className="mb-4 p-3 border border-border rounded-sm bg-muted/5">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Ability Scores</h4>
                  <div className="grid grid-cols-6 gap-2 mb-3">
                    {ABILITIES.map(ab => (
                      <div key={ab} className="text-center">
                        <div className="text-xs font-bold text-muted-foreground">{ab}</div>
                        <div className="text-lg font-bold text-foreground">{selectedCharacter.character_sheet.abilities?.[ab] || 10}</div>
                        <div className="text-xs text-primary">{getModifier(selectedCharacter.character_sheet.abilities?.[ab] || 10)}</div>
                      </div>
                    ))}
                  </div>
                  {selectedCharacter.character_sheet.saving_throws?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-muted-foreground">Saving Throws: </span>
                      <span className="text-xs text-primary">{selectedCharacter.character_sheet.saving_throws.join(", ")}</span>
                    </div>
                  )}
                  {selectedCharacter.character_sheet.skill_proficiencies?.length > 0 && (
                    <div className="mb-2">
                      <span className="text-xs text-muted-foreground">Proficient: </span>
                      <span className="text-xs text-primary">{selectedCharacter.character_sheet.skill_proficiencies.join(", ")}</span>
                    </div>
                  )}
                  {selectedCharacter.character_sheet.spell_slots && (
                    <div className="mb-1"><span className="text-xs text-muted-foreground">Spells/Features: </span><span className="text-xs">{selectedCharacter.character_sheet.spell_slots}</span></div>
                  )}
                  {selectedCharacter.character_sheet.equipment && (
                    <div><span className="text-xs text-muted-foreground">Equipment: </span><span className="text-xs">{selectedCharacter.character_sheet.equipment}</span></div>
                  )}
                </div>
              )}

              {/* PDF Link */}
              {selectedCharacter.pdf_path && (
                <div className="mb-4">
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/files/${selectedCharacter.pdf_path}`, {
                          withCredentials: true, responseType: "blob"
                        });
                        const blob = new Blob([res.data], { type: "application/pdf" });
                        const url = URL.createObjectURL(blob);
                        window.open(url, "_blank");
                      } catch { toast.error("Failed to open PDF"); }
                    }}
                    className="flex items-center gap-2 text-sm text-primary hover:underline cursor-pointer"
                    data-testid="view-pdf-link"
                  >
                    <FileText className="w-4 h-4" /> View Character Sheet PDF
                  </button>
                </div>
              )}

              {/* Description */}
              {selectedCharacter.description && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Description</h4>
                  <div className="text-sm text-foreground/80 prose-sm"><RichTextDisplay content={selectedCharacter.description} /></div>
                </div>
              )}

              {/* Currency */}
              <CurrencyEditor character={selectedCharacter} onChange={(currency) => setSelectedCharacter(prev => prev ? { ...prev, currency } : prev)} />

              {/* Personal notes */}
              {selectedCharacter.notes && (
                <div className="mb-4">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Personal Notes</h4>
                  <div className="text-sm text-foreground/80 whitespace-pre-wrap bg-muted/30 rounded-sm p-3 border border-border">{selectedCharacter.notes}</div>
                </div>
              )}

              {/* Session history feed (Saga20-inspired) */}
              <CharacterSessionHistory characterId={selectedCharacter.id} />

              {/* Session log */}
              {(selectedCharacter.session_notes || []).length > 0 && (
                <div className="mb-4" data-testid="character-session-log">
                  <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Session Log ({selectedCharacter.session_notes.length})
                  </h4>
                  <div className="space-y-2">
                    {selectedCharacter.session_notes.slice().reverse().map((s) => {
                      let dateStr = "";
                      try { dateStr = new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch {}
                      return (
                        <div key={s.id} className="bg-muted/30 border border-border rounded-sm p-2.5">
                          <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                            <span className="truncate">
                              {s.session_name ? <span className="text-primary font-medium">{s.session_name}</span> : "Session"}
                              {s.author ? <span> · {s.author}</span> : null}
                            </span>
                            <span>{dateStr}</span>
                          </div>
                          <p className="text-sm text-foreground/85 whitespace-pre-wrap leading-snug">{s.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-4 border-t border-border">
                <Button size="sm" className="flex-1 rounded-sm" onClick={() => navigate(`/campaign/${campaignId}/characters/${selectedCharacter.id}/sheet`)} data-testid="character-detail-open-sheet">
                  <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Open Sheet
                </Button>
                <Button variant="outline" size="sm" className="flex-1 rounded-sm" onClick={() => openEdit(selectedCharacter)} data-testid="character-detail-edit">
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive rounded-sm" onClick={() => handleDelete(selectedCharacter.id)} data-testid="character-detail-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </SlidePanel>
      </div>
    </Layout>
  );
};

export default CharactersPage;

// ============================================================================
// CurrencyEditor — D&D 5e coin pouch with debounced auto-save.
// ============================================================================
const COINS = [
  { key: "pp", label: "PP", color: "text-cyan-200",  bg: "bg-cyan-500/10",  border: "border-cyan-500/30" },
  { key: "gp", label: "GP", color: "text-yellow-300", bg: "bg-yellow-500/10", border: "border-yellow-500/30" },
  { key: "ep", label: "EP", color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  { key: "sp", label: "SP", color: "text-zinc-200",   bg: "bg-zinc-500/10",  border: "border-zinc-500/30" },
  { key: "cp", label: "CP", color: "text-orange-300", bg: "bg-orange-500/10", border: "border-orange-500/30" },
];

const CurrencyEditor = ({ character, onChange }) => {
  const [coins, setCoins] = useState(character?.currency || { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 });
  const [saving, setSaving] = useState(false);
  const lastSavedRef = useRef(JSON.stringify(character?.currency || {}));
  const saveTimer = useRef(null);

  useEffect(() => {
    setCoins(character?.currency || { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 });
    lastSavedRef.current = JSON.stringify(character?.currency || {});
  }, [character?.id]);

  useEffect(() => {
    if (!character?.id) return;
    if (JSON.stringify(coins) === lastSavedRef.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.patch(`${API}/characters/${character.id}/currency`, coins, { withCredentials: true });
        lastSavedRef.current = JSON.stringify(coins);
        onChange?.(coins);
      } catch { toast.error("Failed to save currency"); }
      finally { setSaving(false); }
    }, 600);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [coins, character?.id]);

  const set = (key, val) => {
    const n = Math.max(0, parseInt(val || 0, 10) || 0);
    setCoins(prev => ({ ...prev, [key]: n }));
  };
  const adjust = (key, delta) => set(key, (coins[key] || 0) + delta);

  // GP-equivalent: 1pp=10gp, 1gp=1, 1ep=0.5gp, 1sp=0.1, 1cp=0.01
  const gpTotal = (coins.pp || 0) * 10 + (coins.gp || 0) + (coins.ep || 0) * 0.5 + (coins.sp || 0) * 0.1 + (coins.cp || 0) * 0.01;

  return (
    <div className="mb-4" data-testid="currency-editor">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
          <Coins className="w-3.5 h-3.5 text-yellow-400" /> Currency
        </h4>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground" data-testid="currency-gp-total">≈ {gpTotal.toFixed(2)} gp</span>
          <span className="text-[9px] text-muted-foreground italic">{saving ? "Saving…" : ""}</span>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {COINS.map(c => (
          <div key={c.key} className={`rounded-sm border ${c.border} ${c.bg} p-2`} data-testid={`currency-${c.key}`}>
            <div className={`text-[10px] uppercase tracking-wider font-bold ${c.color} text-center mb-1`}>{c.label}</div>
            <Input
              type="number"
              min="0"
              value={coins[c.key] ?? 0}
              onChange={(e) => set(c.key, e.target.value)}
              className="h-7 text-center text-sm bg-input border-border font-mono"
              data-testid={`currency-${c.key}-input`}
            />
            <div className="flex gap-1 mt-1">
              <button onClick={() => adjust(c.key, -1)} className="flex-1 text-[9px] py-0.5 rounded-sm bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`currency-${c.key}-down`}>−1</button>
              <button onClick={() => adjust(c.key, 1)} className="flex-1 text-[9px] py-0.5 rounded-sm bg-muted/30 hover:bg-muted/60 border border-border" data-testid={`currency-${c.key}-up`}>+1</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
